/**
 * Movement and qinggong: footsteps on each kind of ground, the jumps (and the third, off the
 * clouds), landing, dashes, running on water and sinking into it, gliding, the flying sword's hum
 * and the eagle.
 */
import type { SfxKit } from '../kit';
import { registerSfx } from '../registry';
import { chime, note, ring, whoosh } from '../voices';

// ───────── footsteps ─────────

/** Kinds of ground a foot can fall on (sfx/frame.ts works out which from the tile). */
export const GROUNDS = ['stone', 'wood', 'grass', 'snow', 'water', 'earth', 'gravel', 'sand', 'moss'] as const;
export type Ground = (typeof GROUNDS)[number];

const STEP: Record<Ground, { label: string; play: (k: SfxKit) => void }> = {
  stone: {
    label: 'ก้าวบนหิน',
    play: (k) => {
      k.click({ f: k.range(2400, 3400), vol: 0.16, q: 2.5 });
      k.thump({ f0: 160, f1: 90, dur: 0.05, vol: 0.12 });
      k.noise({ dur: 0.05, vol: 0.05, type: 'highpass', f: 3000 });
    },
  },
  wood: {
    label: 'ก้าวบนไม้',
    play: (k) => {
      // A hollow plank: a resonant knock.
      k.noise({ dur: 0.07, vol: 0.32, attack: 0.001, f: k.range(420, 560), q: 7 });
      k.thump({ f0: 210, f1: 120, dur: 0.07, vol: 0.14 });
      k.click({ f: 1800, vol: 0.05 });
    },
  },
  grass: {
    label: 'ก้าวบนหญ้า',
    play: (k) => {
      k.noise({ dur: 0.1, vol: 0.12, attack: 0.015, f: k.range(2200, 3200), q: 1.2 });
      k.noise({ color: 'crackle', dur: 0.08, vol: 0.2, type: 'highpass', f: 2000 });
      k.thump({ f0: 110, f1: 70, dur: 0.05, vol: 0.06 });
    },
  },
  snow: {
    label: 'ก้าวบนหิมะ',
    play: (k) => {
      // Packed snow crunching under the foot.
      k.noise({ color: 'crackle', dur: 0.14, vol: 0.55, attack: 0.02, type: 'lowpass', f: 3200 });
      k.noise({ dur: 0.12, vol: 0.08, attack: 0.03, f: 1200, q: 1 });
    },
  },
  earth: {
    label: 'ก้าวบนดินอัด',
    play: (k) => {
      // Packed earth: a dull pat and a little grit.
      k.thump({ f0: 130, f1: 75, dur: 0.06, vol: 0.14 });
      k.noise({ dur: 0.06, vol: 0.1, attack: 0.004, f: k.range(900, 1300), q: 1 });
      k.noise({ color: 'crackle', dur: 0.05, vol: 0.12, type: 'bandpass', f: 2200, q: 0.8 });
    },
  },
  gravel: {
    label: 'ก้าวบนกรวด',
    play: (k) => {
      // Stones rolling under the sole.
      k.noise({ color: 'crackle', dur: 0.13, vol: 0.6, attack: 0.01, type: 'bandpass', f: k.range(2400, 3200), q: 0.7 });
      k.thump({ f0: 140, f1: 80, dur: 0.05, vol: 0.08 });
    },
  },
  sand: {
    label: 'ก้าวบนทราย',
    play: (k) => {
      // A soft hiss sinking in.
      k.noise({ dur: 0.14, vol: 0.12, attack: 0.03, type: 'lowpass', f: k.range(2600, 3400) });
      k.noise({ color: 'crackle', dur: 0.1, vol: 0.12, attack: 0.02, type: 'highpass', f: 3500 });
    },
  },
  moss: {
    label: 'ก้าวบนตะไคร่และกก',
    play: (k) => {
      // Damp and soft: a squelch and reeds brushing.
      k.noise({ dur: 0.1, vol: 0.12, attack: 0.01, f: k.range(600, 800), f1: 400, q: 2 });
      k.noise({ dur: 0.12, vol: 0.06, attack: 0.03, f: 3000, q: 1.5 });
    },
  },
  water: {
    label: 'ก้าวในน้ำ',
    play: (k) => {
      k.noise({ dur: 0.14, vol: 0.2, attack: 0.005, f: 1300, f1: 600, q: 1.2 });
      k.tone({ f: k.range(500, 700), f1: k.range(1000, 1400), at: 0.03, dur: 0.06, vol: 0.04 });
    },
  },
};

for (const g of GROUNDS)
  registerSfx(`move.step.${g}`, { label: STEP[g].label, cat: 'move', vary: 0.1, gap: 0.05, gain: 0.8 }, (k) =>
    STEP[g].play(k),
  );

registerSfx('move.waterRun', { label: 'วิ่งบนน้ำ (วิชาตัวเบา)', cat: 'move', vary: 0.1, gap: 0.05 }, (k) => {
  // Skimming: a light slap and a spray, no sinking.
  k.noise({ dur: 0.1, vol: 0.2, attack: 0.002, f: 2400, f1: 1200, q: 1 });
  k.noise({ at: 0.02, dur: 0.18, vol: 0.08, attack: 0.04, type: 'highpass', f: 4000 });
  k.tone({ f: k.range(700, 900), f1: 1600, at: 0.02, dur: 0.05, vol: 0.03 });
});

// ───────── jumps and landings ─────────

registerSfx('move.jump', { label: 'กระโดด', cat: 'move', vary: 0.06, gap: 0.08 }, (k) => {
  whoosh(k, { dur: 0.18, f0: 400, f1: 1200, vol: 0.18, q: 1.5, peak: 0.25 });
  k.noise({ dur: 0.05, vol: 0.08, f: 1500, q: 1 });
});

registerSfx('move.jump.double', { label: 'กระโดดกลางอากาศ', cat: 'move', vary: 0.05, gap: 0.08 }, (k) => {
  // A kick off the air: a ring of qi, a whoosh, and a little upward chime.
  k.thump({ f0: 200, f1: 400, dur: 0.12, vol: 0.12 });
  whoosh(k, { dur: 0.28, f0: 500, f1: 2600, f2: 1200, vol: 0.2, q: 1.6 });
  k.tone({ f: 880, f1: 1320, at: 0.03, dur: 0.25, vol: 0.035 });
});

registerSfx(
  'move.jump.cloud',
  { label: 'เหยียบเมฆา', cat: 'move', vary: 0.03, gap: 0.1, preview: 1 },
  (k) => {
    k.noise({ color: 'pink', dur: 0.5, vol: 0.22, attack: 0.08, type: 'lowpass', f: 900, f1: 400 });
    whoosh(k, { dur: 0.3, f0: 600, f1: 2800, f2: 1300, vol: 0.16, q: 1.4 });
    chime(k, { f: note(1174.7, 2), at: 0.05, vol: 0.04, dur: 0.7 });
  },
);

registerSfx('move.land', { label: 'ลงพื้น', cat: 'move', vary: 0.06, gap: 0.1 }, (k) => {
  k.thump({ f0: 125, f1: 48, dur: 0.14, vol: 0.32 });
  k.noise({ color: 'brown', dur: 0.14, vol: 0.3, type: 'lowpass', f: 700, f1: 200 });
  k.noise({ at: 0.02, dur: 0.15, vol: 0.05, attack: 0.03, f: 2000, q: 0.8 });
});

registerSfx('move.splash', { label: 'ตกน้ำ', cat: 'move', vary: 0.06, gap: 0.2, preview: 1.3 }, (k) => {
  k.noise({ dur: 0.5, vol: 0.4, attack: 0.005, f: 1500, f1: 400, q: 0.8 });
  k.noise({ color: 'brown', dur: 0.4, vol: 0.4, type: 'lowpass', f: 500 });
  for (let n = 0; n < 6; n++)
    k.tone({ f: k.range(300, 600), f1: k.range(900, 1600), at: 0.15 + k.r() * 0.6, dur: 0.07, vol: 0.04 });
});

registerSfx('move.airReset', { label: 'ถีบตัวกลางอากาศ', cat: 'move', vary: 0.04, gap: 0.1 }, (k) => {
  k.thump({ f0: 180, f1: 300, dur: 0.1, vol: 0.12 });
  chime(k, { f: 1568, vol: 0.05, dur: 0.6 });
});

// ───────── dashes ─────────

registerSfx('move.dash', { label: 'พุ่งตัว', cat: 'move', vary: 0.06, gap: 0.06 }, (k) => {
  whoosh(k, { dur: 0.24, f0: 300, f1: 1900, f2: 700, vol: 0.28, q: 1.5, peak: 0.25 });
  k.noise({ dur: 0.08, vol: 0.06, type: 'highpass', f: 3500 });
});

registerSfx('move.dash.skill', { label: 'พุ่งด้วยกระบวนท่า', cat: 'move', vary: 0.05, gap: 0.06 }, (k) => {
  k.noise({ dur: 0.2, vol: 0.26, attack: 0.04, f: 700, f1: 4200, q: 2.5 });
  whoosh(k, { dur: 0.3, f0: 200, f1: 900, f2: 300, vol: 0.14, q: 1, color: 'pink' });
});

registerSfx('move.blink', { label: 'หายตัว (วาร์ป)', cat: 'move', vary: 0.05, gap: 0.05 }, (k) => {
  k.noise({ dur: 0.2, vol: 0.28, attack: 0.18, f: 500, f1: 4000, q: 2, linear: true });
  k.click({ f: 4000, at: 0.2, vol: 0.12 });
});

// ───────── glide, mounts ─────────

registerSfx('move.glide.open', { label: 'กางร่อนลม', cat: 'move', vary: 0.04, gap: 0.2 }, (k) => {
  // Robes catching the wind: two quick cloth flaps.
  for (const at of [0, 0.09]) k.noise({ at, dur: 0.1, vol: 0.2, attack: 0.01, f: 900, f1: 400, q: 0.9 });
  whoosh(k, { at: 0.05, dur: 0.4, f0: 300, f1: 900, vol: 0.1, q: 1 });
});

registerSfx(
  'move.glide',
  { label: 'ลมขณะร่อน (วน)', cat: 'move', loop: true, vary: 0.03, preview: 2.5 },
  (k) => {
    k.noise({ color: 'pink', dur: Infinity, vol: 0.18, attack: 0.4, f: 700, q: 0.8, trem: [0.7, 0.5] });
    k.noise({ dur: Infinity, vol: 0.03, attack: 0.6, f: 2600, q: 3, trem: [1.3, 0.6] });
  },
);

registerSfx(
  'move.swordFlight',
  { label: 'เสียงหึ่งกระบี่เหาะ (วน)', cat: 'move', loop: true, vary: 0.02, preview: 2.5 },
  (k) => {
    // The flying sword humming under the feet, with the wind of the flight.
    k.tone({ f: 220, dur: Infinity, vol: 0.05, attack: 0.3, vib: [5.5, 0.006] });
    k.tone({ f: 330.5, dur: Infinity, vol: 0.03, attack: 0.4, vib: [4.7, 0.006] });
    k.tone({ f: 1760, dur: Infinity, vol: 0.006, attack: 0.5, vib: [7, 0.004] });
    k.noise({ color: 'pink', dur: Infinity, vol: 0.09, attack: 0.5, f: 900, q: 1, trem: [0.4, 0.4] });
  },
);

registerSfx(
  'move.mount.sword',
  { label: 'เรียกกระบี่เหาะ', cat: 'move', vary: 0.03, gap: 0.3, preview: 1 },
  (k) => {
    k.noise({ dur: 0.35, vol: 0.18, attack: 0.25, f: 1200, f1: 5800, q: 6 });
    ring(k, { f: 2640, at: 0.3, dur: 0.6, vol: 0.05 });
    whoosh(k, { at: 0.2, dur: 0.35, f0: 400, f1: 1600, f2: 600, vol: 0.15 });
  },
);

registerSfx(
  'move.mount.eagle',
  { label: 'นกอินทรีร้อง', cat: 'move', vary: 0.04, gap: 0.5, preview: 1.4 },
  (k) => {
    // The eagle's cry: a screech falling at the end, roughened by fast vibrato.
    const bp = k.filter('bandpass', 2600, 1.5);
    k.fm({
      f: 2100,
      f1: 1700,
      ratio: 0.5,
      index: 0.6,
      dur: 0.75,
      vol: 0.12,
      attack: 0.04,
      type: 'sawtooth',
      out: bp,
    });
    k.tone({ f: 2300, f1: 1800, dur: 0.7, vol: 0.05, attack: 0.04, vib: [38, 0.02] });
    k.tone({ f: 2500, f1: 2000, at: 0.85, dur: 0.35, vol: 0.04, attack: 0.02, vib: [38, 0.02] });
    flap(k, 0.05);
  },
);

registerSfx('move.eagle.flap', { label: 'ปีกอินทรีกระพือ', cat: 'move', vary: 0.08, gap: 0.2 }, (k) =>
  flap(k, 0),
);

registerSfx('move.dismount', { label: 'ลงจากพาหนะ', cat: 'move', vary: 0.05, gap: 0.3 }, (k) => {
  whoosh(k, { dur: 0.3, f0: 1600, f1: 400, vol: 0.14, q: 1.2, peak: 0.2 });
  k.thump({ f0: 120, f1: 50, at: 0.24, dur: 0.12, vol: 0.25 });
});

/** A great wing's beat: two heavy pushes of air. */
function flap(k: SfxKit, at: number): void {
  for (const d of [0, 0.07])
    k.noise({
      color: 'brown',
      at: at + d,
      dur: 0.16,
      vol: 0.4,
      attack: 0.03,
      type: 'lowpass',
      f: 600,
      f1: 250,
    });
  k.noise({ at, dur: 0.2, vol: 0.05, attack: 0.05, f: 1400, q: 1 });
}
